import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  Search, 
  Filter,
  Plus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Download,
  RefreshCw,
  Eye,
  Phone,
  Mail,
  MapPin,
  MoreVertical
} from 'lucide-react';
import { Card, Button, Badge, Input, Select, Modal } from '../../components/ui';
import AdminSidebar from '../../components/layout/AdminSidebar';
import { appointmentsAPI, patientsAPI, doctorsAPI, servicesAPI, realTimeUpdates } from '../../services/api';
import { formatDate, formatTime, getRelativeDate } from '../../utils';
import { Appointment, Patient } from '../../types';
import toast from 'react-hot-toast';

const AdminAppointments: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [doctors, setDoctors] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Load all data from API
  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const [appointmentsResult, patientsResult, doctorsResult, servicesResult] = await Promise.all([
        appointmentsAPI.getAll(),
        patientsAPI.getAll(),
        doctorsAPI.getAll(),
        servicesAPI.getAll()
      ]);

      if (appointmentsResult.success && appointmentsResult.data) {
        setAppointments(appointmentsResult.data);
      } else {
        setError(appointmentsResult.error || 'Failed to load appointments');
      }

      if (patientsResult.success && patientsResult.data) {
        setPatients(patientsResult.data);
      }

      if (doctorsResult.success && doctorsResult.data) {
        setDoctors(doctorsResult.data);
      }

      if (servicesResult.success && servicesResult.data) {
        setServices(servicesResult.data);
      }
    } catch (err) {
      setError('Network error while loading data');
      console.error('Error loading appointments data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Set up real-time updates
  useEffect(() => {
    const handleAppointmentsUpdate = (updatedAppointments: Appointment[]) => {
      setAppointments(updatedAppointments);
    };

    // Subscribe to real-time updates
    realTimeUpdates.subscribe('appointments', handleAppointmentsUpdate, 30000);

    return () => {
      realTimeUpdates.unsubscribe('appointments');
    };
  }, []);

  // Action handlers
  const handleStatusChange = async (appointmentId: string, newStatus: Appointment['status']) => {
    try {
      const result = await appointmentsAPI.update(appointmentId, { status: newStatus });
      
      if (result.success) {
        setAppointments(prev => prev.map(apt => 
          apt.id === appointmentId ? { ...apt, status: newStatus } : apt
        ));
        toast.success(`Appointment ${newStatus}`);
      } else {
        toast.error(result.error || 'Failed to update appointment');
      }
    } catch (err) {
      toast.error('Network error while updating appointment');
    }
  };

  const handleDeleteAppointment = async (appointmentId: string) => {
    if (window.confirm('Are you sure you want to delete this appointment?')) {
      try {
        const result = await appointmentsAPI.delete(appointmentId);
        
        if (result.success) {
          setAppointments(prev => prev.filter(apt => apt.id !== appointmentId));
          toast.success('Appointment deleted');
        } else {
          toast.error(result.error || 'Failed to delete appointment');
        }
      } catch (err) {
        toast.error('Network error while deleting appointment');
      }
    }
  };

  const handleViewDetails = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setShowDetails(true);
  };

  const handleEditAppointment = (appointment: Appointment) => {
    // For now, we'll just show details. In a real app, this would open an edit form
    setSelectedAppointment(appointment);
    setShowDetails(true);
    toast('Edit functionality will be available soon', { icon: 'ℹ️' });
  };

  const filterOptions = [
    { value: 'all', label: 'All Appointments' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const dateOptions = [
    { value: 'all', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' }
  ];

  // Filter appointments
  const filteredAppointments = appointments.filter(appointment => {
    const matchesSearch = searchTerm === '' || 
      appointment.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      appointment.patientId.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || appointment.status === statusFilter;
    
    const appointmentDate = new Date(appointment.date);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let matchesDate = true;
    if (dateFilter === 'today') {
      matchesDate = appointmentDate.toDateString() === today.toDateString();
    } else if (dateFilter === 'tomorrow') {
      matchesDate = appointmentDate.toDateString() === tomorrow.toDateString();
    } else if (dateFilter === 'week') {
      const weekFromNow = new Date(today);
      weekFromNow.setDate(weekFromNow.getDate() + 7);
      matchesDate = appointmentDate >= today && appointmentDate <= weekFromNow;
    } else if (dateFilter === 'month') {
      const monthFromNow = new Date(today);
      monthFromNow.setMonth(monthFromNow.getMonth() + 1);
      matchesDate = appointmentDate >= today && appointmentDate <= monthFromNow;
    }
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const getPatient = (patientId: string): Patient | undefined => {
    return patients.find(p => p.id === patientId);
  };

  const getDoctor = (doctorId: string) => {
    return doctors.find(d => d.id === doctorId);
  };

  const getService = (serviceId: string) => {
    return services.find(s => s.id === serviceId);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'confirmed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'cancelled': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'completed': return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'scheduled': return <Clock className="w-4 h-4 text-yellow-600" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'cancelled': return 'danger';
      case 'completed': return 'info';
      case 'scheduled': return 'warning';
      default: return 'primary';
    }
  };





  const stats = [
    {
      title: "Today's Appointments",
      value: filteredAppointments.filter(apt => 
        new Date(apt.date).toDateString() === new Date().toDateString()
      ).length,
      icon: Calendar,
      color: 'bg-blue-500'
    },
    {
      title: 'Confirmed',
      value: filteredAppointments.filter(apt => apt.status === 'confirmed').length,
      icon: CheckCircle,
      color: 'bg-green-500'
    },
    {
      title: 'Pending',
      value: filteredAppointments.filter(apt => apt.status === 'scheduled').length,
      icon: Clock,
      color: 'bg-yellow-500'
    },
    {
      title: 'Completed',
      value: filteredAppointments.filter(apt => apt.status === 'completed').length,
      icon: Users,
      color: 'bg-purple-500'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <AdminSidebar isOpen={sidebarOpen} toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
      
      <div className={`transition-all duration-300 ${sidebarOpen ? 'ml-72' : 'ml-20'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Appointments Management</h1>
                <p className="text-gray-600">Manage and monitor all clinic appointments</p>
              </div>
              
              <div className="flex items-center space-x-4">
                <Button onClick={() => window.location.reload()}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  New Appointment
                </Button>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <Card key={index} className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Filters */}
          <Card className="mb-6">
            <div className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
                <div className="flex flex-col sm:flex-row sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 flex-1">
                  <div className="relative flex-1 max-w-md">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search appointments, patients..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Filter className="w-4 h-4 text-gray-400" />
                    <Select
                      options={filterOptions}
                      value={statusFilter}
                      onChange={setStatusFilter}
                      className="min-w-40"
                    />
                    <Select
                      options={dateOptions}
                      value={dateFilter}
                      onChange={setDateFilter}
                      className="min-w-32"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">
                    {filteredAppointments.length} appointments
                  </span>
                  <Button variant="outline" size="sm">
                    <Download className="w-4 h-4 mr-1" />
                    Export
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Appointments Table */}
          <Card>
            <div className="p-6">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                  <span className="text-gray-600">Loading appointments...</span>
                </div>
              ) : filteredAppointments.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Patient</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Date & Time</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Doctor</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Service</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAppointments.map((appointment) => {
                        const patient = getPatient(appointment.patientId);
                        const doctor = getDoctor(appointment.doctorId);
                        const service = getService(appointment.serviceId);
                        
                        return (
                          <tr key={appointment.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
                                  <Users className="w-4 h-4 text-gray-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown Patient'}
                                  </p>
                                  <p className="text-sm text-gray-600">ID: {appointment.patientId}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {getRelativeDate(appointment.date)}
                                </p>
                                <p className="text-sm text-gray-600">
                                  {formatTime(appointment.time)} ({appointment.duration}min)
                                </p>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <p className="font-medium text-gray-900">
                                {doctor?.name || 'Unknown Doctor'}
                              </p>
                              <p className="text-sm text-gray-600">
                                {doctor?.specialty || 'General'}
                              </p>
                            </td>
                            <td className="py-4 px-4">
                              <p className="font-medium text-gray-900">
                                {service?.name || 'General Consultation'}
                              </p>
                              <p className="text-sm text-gray-600">
                                {service?.category || 'General'}
                              </p>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-2">
                                {getStatusIcon(appointment.status)}
                                <Badge variant={getStatusVariant(appointment.status) as any}>
                                  {appointment.status}
                                </Badge>
                              </div>
                            </td>
                            <td className="py-4 px-4">
                              <div className="flex items-center space-x-2">
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleViewDetails(appointment)}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleEditAppointment(appointment)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => handleDeleteAppointment(appointment.id)}
                                >
                                  <Trash2 className="w-4 h-4 text-red-600" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No appointments found</h3>
                  <p className="text-gray-600 mb-6">
                    {searchTerm || statusFilter !== 'all' || dateFilter !== 'all'
                      ? "No appointments match your current filters."
                      : "No appointments have been scheduled yet."
                    }
                  </p>
                  <Button>
                    <Plus className="w-4 h-4 mr-2" />
                    Schedule New Appointment
                  </Button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* Appointment Details Modal */}
      {showDetails && selectedAppointment && (
        <Modal
          isOpen={showDetails}
          onClose={() => setShowDetails(false)}
          title="Appointment Details"
        >
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Patient Information</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Name:</span> {getPatient(selectedAppointment.patientId)?.firstName} {getPatient(selectedAppointment.patientId)?.lastName}</p>
                  <p><span className="font-medium">Email:</span> {getPatient(selectedAppointment.patientId)?.email}</p>
                  <p><span className="font-medium">Phone:</span> {getPatient(selectedAppointment.patientId)?.phone}</p>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Appointment Details</h4>
                <div className="space-y-2">
                  <p><span className="font-medium">Date:</span> {formatDate(selectedAppointment.date)}</p>
                  <p><span className="font-medium">Time:</span> {formatTime(selectedAppointment.time)}</p>
                  <p><span className="font-medium">Duration:</span> {selectedAppointment.duration} minutes</p>
                  <p><span className="font-medium">Status:</span> {selectedAppointment.status}</p>
                </div>
              </div>
            </div>
            
            {selectedAppointment.notes && (
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Notes</h4>
                <p className="text-gray-700">{selectedAppointment.notes}</p>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <Button variant="outline" onClick={() => setShowDetails(false)}>
                Close
              </Button>
              <Button onClick={() => {
                setShowDetails(false);
                handleEditAppointment(selectedAppointment);
              }}>
                Edit Appointment
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminAppointments;